import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ScanLine, Loader2, Cog, Flower2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { StartForm } from "../components/bloom/StartForm";
import { Passport } from "../components/bloom/Passport";
import { Barcode } from "../components/bloom/BloomBits";
import { Sparkle } from "../components/Decor2";
import { WavyBreak } from "../components/Wavy";

const BloomId = () => {
  const [codeInput, setCodeInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState("idle"); // idle | found | create
  const [record, setRecord] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pendingCode, setPendingCode] = useState("");
  const [searchParams] = useSearchParams();

  const lookup = async (raw) => {
    const code = raw.trim().toUpperCase().replace(/\s+/g, "-");
    if (!code) { toast.error("Type the Bloom ID from your plant tag first"); return; }
    setScanning(true);
    try {
      const { data } = await api.get(`/bloom/${encodeURIComponent(code)}`);
      setRecord(data.record);
      setEntries(data.entries);
      setMode("found");
      toast.success("Passport found — welcome back!");
    } catch (err) {
      if (err.response?.status === 404) {
        setPendingCode(code);
        setMode("create");
      } else {
        toast.error(err.response?.data?.detail || "The scanner jammed — try again");
      }
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    const qp = searchParams.get("code");
    if (qp) { setCodeInput(qp.toUpperCase()); lookup(qp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scan = (e) => {
    e.preventDefault();
    lookup(codeInput);
  };

  const reset = () => { setMode("idle"); setRecord(null); setEntries([]); setCodeInput(""); };

  return (
    <div className="bg-cream">
      <section className="relative pt-10 md:pt-16 pb-12 px-6 md:px-10 text-center overflow-hidden">
        <Sparkle className="absolute top-16 left-14 w-6 h-6" color="#2E7D4F" />
        <Sparkle className="absolute top-28 right-24 w-5 h-5" color="#FFD23F" />
        <Cog className="absolute top-14 right-14 w-10 h-10 text-magenta/25 animate-[spin_9s_linear_infinite]" aria-hidden />
        <Cog className="absolute bottom-4 left-16 w-7 h-7 text-forest/25 animate-[spin_12s_linear_infinite_reverse]" aria-hidden />
        <span className="font-serif-display uppercase tracking-[0.3em] text-forest text-[11px] md:text-sm font-semibold">THE FOUNDRY IDENTIFICATION ENGINE</span>
        <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]" data-testid="bloom-title">
          BLOOM ID
        </h1>
        <p className="mt-3 font-serif-display uppercase tracking-[0.2em] text-magenta text-sm md:text-base font-semibold">YOUR PLANT'S DIGITAL PASSPORT</p>
        <p className="mt-5 text-plum-deep/80 text-base md:text-lg max-w-2xl mx-auto">
          Enter the unique code from your plant tag, upload a few photos and tell us what you're seeing.
          We'll help identify your plant and keep a record of its journey as it grows.
        </p>

        <form onSubmit={scan} className="mt-9 max-w-xl mx-auto" data-testid="bloom-scanner">
          <div className="relative bg-plum-deep rounded-[2rem] p-6 md:p-7 shadow-[0_25px_50px_-20px_rgba(74,27,77,0.6)] text-left">
            <span className="absolute top-3 left-4 w-2 h-2 rounded-full bg-cream/25" aria-hidden />
            <span className="absolute top-3 right-4 w-2 h-2 rounded-full bg-cream/25" aria-hidden />
            <span className="absolute bottom-3 left-4 w-2 h-2 rounded-full bg-cream/25" aria-hidden />
            <span className="absolute bottom-3 right-4 w-2 h-2 rounded-full bg-cream/25" aria-hidden />
            <p className="text-[10px] uppercase tracking-[0.3em] text-lemon font-bold text-center">INSERT BLOOM ID BELOW</p>
            <div className="mt-3 flex gap-3 flex-col sm:flex-row">
              <input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="E.G. BF-2026-0417"
                maxLength={30}
                data-testid="bloom-code-input"
                className="flex-1 rounded-xl px-5 py-4 bg-[#2E0F30] text-lemon placeholder:text-cream/25 font-mono tracking-[0.28em] text-base text-center sm:text-left focus:outline-none border-2 border-cream/10 focus:border-lemon shadow-[inset_0_3px_8px_rgba(0,0,0,0.6)]"
              />
              <button
                type="submit"
                disabled={scanning}
                data-testid="bloom-scan-btn"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-lemon text-plum-deep px-7 py-4 font-serif-display uppercase text-sm tracking-[0.2em] font-bold hover:bg-cream transition-colors disabled:opacity-60"
              >
                {scanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <ScanLine className="w-5 h-5" />}
                {scanning ? "SCANNING" : "SCAN"}
              </button>
            </div>
            <Barcode className="mt-4 text-cream/15" />
          </div>
        </form>

        {mode !== "idle" && (
          <button onClick={reset} data-testid="bloom-reset-btn" className="mt-5 text-xs uppercase tracking-[0.2em] text-plum-deep/50 hover:text-magenta font-semibold underline underline-offset-4">
            SCAN A DIFFERENT BLOOM ID
          </button>
        )}
      </section>

      <WavyBreak color="#2E7D4F" />

      <section className="px-6 md:px-10 py-12 max-w-3xl mx-auto">
        {mode === "idle" && (
          <div className="grid sm:grid-cols-3 gap-4 text-center" data-testid="bloom-how-it-works">
            {[
              { n: "01", title: "ENTER BLOOM ID", copy: "Type the unique code printed on your plant tag into the scanner." },
              { n: "02", title: "UPLOAD PHOTOS", copy: "Snap the leaves, buds and blooms — every photo helps identification." },
              { n: "03", title: "ADD PLANT DETAILS", copy: "Log colour, shape and growth. Come back anytime to update its story." },
            ].map((s) => (
              <div key={s.n} className="bg-warmwhite border-2 border-dashed border-plum-deep/15 rounded-3xl p-6">
                <span className="font-mono text-magenta text-lg font-bold">{s.n}</span>
                <h3 className="font-serif-display uppercase text-plum-deep mt-2 tracking-[0.1em]">{s.title}</h3>
                <p className="mt-2 text-xs text-plum-deep/60 leading-relaxed">{s.copy}</p>
              </div>
            ))}
            <div className="sm:col-span-3 flex items-center justify-center gap-2 text-plum-deep/50 text-xs uppercase tracking-[0.18em] mt-2">
              <Flower2 className="w-4 h-4 text-forest" /> Every passport stays on record — return each season and keep the story growing
            </div>
          </div>
        )}
        {mode === "create" && <StartForm code={pendingCode} onCreated={(d) => { setRecord(d.record); setEntries(d.entries); setMode("found"); }} />}
        {mode === "found" && record && (
          <Passport record={record} entries={entries} onEntryAdded={(entry) => setEntries((l) => [entry, ...l])} />
        )}
      </section>
    </div>
  );
};

export default BloomId;
